import React, { useContext, createContext, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../hooks";
import { liveAddToQueue, liveUpdateRequests } from "../slices/requests";

// Create context for provider
const ActionableRequestsContext = createContext<{
  isOnline: boolean;
  sendSocketMessage: (e: any, message: any, body: any) => void;
}>({ isOnline: false, sendSocketMessage: () => { } });
export function useActionableRequests() {
  return useContext(ActionableRequestsContext);
}

interface ActionableRequestsProviderProps {
  children: React.ReactNode;
}

// Create provider
export default function ActionableRequestsProvider({
  children,
}: ActionableRequestsProviderProps) {
  const dispatch = useDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Create state to store whether the user is online or not
  const [isOnline, setOnlineStatus] = useState(false);

  // Instantiate and open Websocket connection
  let socket: any;
  let reconnectOnClose = true;
  let requestUrl: string = "";
  let domain: string[];
  let domainRoot: string;
  if (window.location.hostname === "localhost") {
    requestUrl = `ws://${window.location.hostname}:8000/ws/request/${user?.id}/`;
  } else if (window.location.hostname.startsWith("192.")) {
    requestUrl = `ws://${window.location.hostname}:30080/ws/request/${user?.id}/`;
  } else if (window.location.hostname === "local.audico.co.uk") {
    domain = window.location.hostname.split("www.");
    domainRoot = domain[domain.length - 1];
    requestUrl = `ws://${domainRoot}/ws/request/${user?.id}/`;
  } else if (window.location.hostname.includes("audico.co.uk")) {
    domain = window.location.hostname.split("www.");
    domainRoot = domain[domain.length - 1];
    requestUrl = `wss://${domainRoot}/ws/request/${user?.id}/`;
  }

  function buildSocketConnection() {
    if (!requestUrl.includes("undefined")) {
      socket = new WebSocket(requestUrl);
    }

    if (socket !== undefined) {
      // When socket is opened
      socket.onopen = (e: any) => {
        console.log(`[open] socket connection opened ${JSON.stringify(e)}`);
        setOnlineStatus(true);
      };

      // Close without reconnecting;
      const close = socket.close;
      socket.close = () => {
        reconnectOnClose = false;
        close.call(socket);
      };

      // When socket is closed
      socket.onclose = (e: any) => {
        if (!reconnectOnClose) {
          console.log(
            `[close] socket connection closed by app, code=${e.code} reason=${e.reason}`,
          );
          return;
        }
        console.log(
          `[close] socket connection closed by server, code=${e.code} reason=${e.reason}`,
        );
        setTimeout(buildSocketConnection, 5000);
      };

      // When socket has error
      socket.onerror = (e: any) => {
        console.log(
          `[error] socket connection has failed, code=${e.code} reason=${e.reason}`,
        );
        socket.close(1000, e);
        setOnlineStatus(false);
      };

      socket.onmessage = (e: any) => {
        console.log("[message] socket connection received data");
        const data = JSON.parse(e.data);
        console.log("received data: ", data);
        const messageType = data.type;

        // If updating existing requests
        if (messageType === "update.request") {
          const requests = data.requests;
          dispatch(liveUpdateRequests(requests));
        }

        // Otherwise, if its a new request
        else if (messageType === "new.request") {
          const view = user === null ? [] : user.view;
          const request = data.request;
          if (view.includes(request.category[0])) {
            dispatch(liveAddToQueue(request));
          }
        }
      };
    }
  }

  buildSocketConnection();
  const value = useMemo(() => {
    // Function to send socket message
    function sendSocketMessage(e: any, message: any, body: any) {
      e.preventDefault();
      console.log("[send] socket connection sending data");
      console.log(body);

      // Send socket message
      socket?.send(
        JSON.stringify({
          message,
          data: {
            id: body.id,
            datetime_created: body.datetime_created,
            datetime_updated: body.datetime_updated,
            category: body.category,
            request: body.request,
            details: body.details,
            quantity: body.quantity,
            room: body.room,
            status: body.status,
          },
        }),
      );
    }

    return { isOnline, sendSocketMessage };
  }, [isOnline, socket]);

  // const socket = useMemo(() => {
  //   if (!requestUrl.includes("undefined")) {
  //     return new WebSocket(requestUrl);
  //   }
  // }, [requestUrl]);

  // ////////////////////
  // // REQUEST SOCKET //
  // ////////////////////

  // // When message is received from socket
  // const view = user === null ? [] : user.view
  // if (socket !== undefined) {
  //   socket.onmessage = (e) => {
  //     console.log("[message] socket connection received data");
  //     const data = JSON.parse(e.data);
  //     console.log("received data: ", data)
  //     const messageType = data.type;

  //     // If updating existing requests
  //     if (messageType === "update.request") {
  //       const requests = data.requests;
  //       dispatch(liveUpdateRequests(requests));
  //     }

  //     // Otherwise, if its a new request
  //     else if (messageType === "new.request") {
  //       const request = data.request;
  //       if (view.includes(request.category[0])) {
  //         dispatch(liveAddToQueue(request));
  //       }
  //     }

  //   };

  //   // When socket is opened
  //   socket.onopen = (e) => {
  //     console.log(
  //       `[open] socket connection opened ${JSON.stringify(e)}`
  //     );
  //     setOnlineStatus(true);
  //   };

  //   // When socket is closed
  //   socket.onclose = (e) => {
  //     console.log(
  //       `[close] socket connection closed cleanly, code=${e.code} reason=${e.reason}`
  //     );
  //     setOnlineStatus(false);
  //   };

  //   // When socket has error
  //   socket.onerror = (e: any) => {
  //     console.log(
  //       `[error] socket connection has failed, code=${e.code} reason=${e.reason}`
  //     );
  //     socket.close(1000, e);
  //     setOnlineStatus(false);
  //   };
  // }

  // // Create final object to be passed down in provider
  // const value = useMemo(() => {

  //   // Function to send socket message
  //   function sendSocketMessage(e: any, message: any, body: any) {
  //     e.preventDefault();
  //     console.log("[send] socket connection sending data");
  //     console.log(body);

  //     // Send socket message
  //     socket?.send(
  //       JSON.stringify({
  //         message,
  //         data: {
  //           id: body.id,
  //           datetime_created: body.datetime_created,
  //           datetime_updated: body.datetime_updated,
  //           category: body.category,
  //           request: body.request,
  //           details: body.details,
  //           quantity: body.quantity,
  //           room: body.room,
  //           status: body.status,
  //         }
  //       })
  //     );
  //   }

  //   return { isOnline, sendSocketMessage };
  // }, [isOnline, socket]);

  return (
    <ActionableRequestsContext.Provider value={value}>
      {children}
    </ActionableRequestsContext.Provider>
  );
}
